import tinycolor from 'tinycolor2';

/**
 * The colours of the manual fugue analysis in the paper (Figure 2), measured from its highlighter marks: the subject
 * yellow, the countersubjects light blue and pink, the canon orange, the sequences in red ink. The pink is a little
 * deeper than the figure's (#f890e8) and the light blue a little more saturated (#80c8f0): as measured, the two were
 * almost the same for red-green colour-blind viewers. Motifs, which the figure does not colour, take teal and indigo,
 * then grey.
 *
 * Checked with the dataviz palette validator (all pairs): colour-vision separation at least ΔE 9.1, normal vision at
 * least ΔE 21. Highlighter colours are light (contrast below 3:1 on white), so every pattern also carries its label
 * (chips in the sheet, timeline marks, cards) and the family view lists its entries in a table (audit F07).
 */
export const highlighter = {
  subject: '#f8f868',
  firstCountersubject: '#78c8f8',
  secondCountersubject: '#e468d0',
  canon: '#f09018',
  sequence: '#b80010',
  firstMotif: '#008f8a',
  secondMotif: '#6a5acd'
};

/** Patterns beyond the coloured ones fold into one neutral colour; their labels keep them apart. */
export const otherColor = '#898781';

/**
 * Colours of the analyst's own patterns in the order an analyst marks a fugue (Figure 2): the first pattern (usually
 * the subject) yellow, then the countersubjects, the canon, the sequences, further motifs.
 */
export const patternPalette = [
  highlighter.subject,
  highlighter.firstCountersubject,
  highlighter.secondCountersubject,
  highlighter.canon,
  highlighter.sequence,
  highlighter.firstMotif,
  highlighter.secondMotif
];

/**
 * A shade of the same hue, for a variant of a pattern or a further pattern of the same role: darker for the light
 * highlighter colours (lighter ones would fade into the white page), lighter for dark ones. Shades of one colour are
 * always taken from that colour with more `steps`: shading a shade can turn back to the original colour.
 */
export const shade = (color: string, steps = 1) => {
  const base = tinycolor(color);
  return (base.isLight() ? base.darken(14 * steps) : base.lighten(12 * steps)).toHexString();
};

/**
 * The categorical palette before the Figure 2 colours (until 2026-10-02). Analyses saved with it are migrated: each
 * automatic colour (a slot, or a variant of it, lightened by 12 per derivation) maps to the Figure 2 colour of its slot
 * and as many shades; the eighth slot folds into grey like any pattern beyond the palette.
 */
const legacyPalette = [
  '#2a78d6',
  '#eb6834',
  '#1baf7a',
  '#eda100',
  '#e87ba4',
  '#008300',
  '#4a3aa7',
  '#e34948'
];
const legacyColors = new Map<string, string>();
[...legacyPalette, otherColor].forEach((legacy, slot) => {
  const current = slot < patternPalette.length ? patternPalette[slot] : otherColor;
  let variant = legacy;
  for (let steps = 0; steps <= 3; steps += 1) {
    // Lightened three times, the lightest slots reach white, which no automatic colour of today's palette is.
    if (variant !== '#ffffff') legacyColors.set(variant, shade(current, steps));
    variant = tinycolor(variant).lighten(12).toHexString();
  }
});

/** Whether the earlier palette assigned the colour automatically. */
export const isLegacyColor = (color: string) => legacyColors.has(tinycolor(color).toHexString());

/** The Figure 2 colour of a colour the earlier palette assigned; any other colour (e.g. chosen by the analyst) stays. */
export const fromLegacyPalette = (color: string) =>
  legacyColors.get(tinycolor(color).toHexString()) ?? color;

/** A colour dark enough for thin lines and text-like marks on white (e.g. the contour of a pattern). */
export const lineColor = (color: string) =>
  tinycolor(color).isLight() ? tinycolor(color).darken(25).toHexString() : color;
